/**
 * Scheduling controls that sit on a machine wherever it shows: its labels, edited inline, and
 * Disable new work (the drain: running work finishes) with an undo toast. Used by the Machines cards and
 * the Capacity page.
 */
import { Check, Pencil, RotateCcw, X } from '@orgus/ui/icons';
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { LABEL_RE, parseLabels } from '@orgus/protocol';
import type { Machine } from '@orgus/protocol';
import { Badge, IconButton, Input, Tooltip, cn, toast } from '@orgus/ui';
import { drainNode, resumeNode, setNode, useSchedulerState } from './data.ts';
import { nodeView } from './model.ts';

/** The labels the scheduler uses for a machine; click the pencil (or the labels) to edit them inline. */
export function NodeLabels({ machine: m, max = 4, className, align = 'end', wrap = false }: { machine: Machine; max?: number; className?: string; align?: 'start' | 'end'; wrap?: boolean }) {
  const { state } = useSchedulerState();
  const v = nodeView(m, state);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const own = v.labels.filter((l) => l !== m.os);

  useEffect(() => {
    if (editing) {
      setText(own.join(', '));
      setError(null);
      requestAnimationFrame(() => inputRef.current?.select());
    }
    // Only when editing starts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  if (!state) {
    // Older hub without the scheduler: show what the agent reports, read only.
    return (
      <span className={cn('flex min-w-0 items-center gap-1', className)}>
        {m.labels.slice(0, max).map((l) => (
          <Badge key={l} variant="outline">
            {l}
          </Badge>
        ))}
      </span>
    );
  }

  const save = async () => {
    const words = text.split(/[\s,]+/).filter(Boolean);
    const badWord = words.find((w) => !LABEL_RE.test(w.toLowerCase()));
    if (badWord) {
      setError(`"${badWord}" is not a label. Use letters, digits, dot, dash, colon or slash.`);
      return;
    }
    const next = parseLabels(text);
    const before = state.nodes[m.id]?.labels ?? null;
    setBusy(true);
    try {
      await setNode(m.id, { labels: next });
      setEditing(false);
      toast('Labels saved', {
        description: next.length ? `${m.name}: ${next.join(', ')}` : `${m.name} has no labels beyond its OS.`,
        action: { label: 'Undo', onClick: () => void setNode(m.id, { labels: before }).catch((e) => toast.error(e)) },
      });
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await setNode(m.id, { labels: null });
      setEditing(false);
      toast('Labels reset', { description: `${m.name} uses the labels its agent reports again.` });
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e: KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div className={cn('flex min-w-0 flex-1 items-start gap-1', className)} onClick={(e) => e.stopPropagation()}>
        <Input
          ref={inputRef}
          size="sm"
          aria-label={`Labels for ${m.name}, separated by commas`}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setError(null);
          }}
          onKeyDown={onKey}
          placeholder="laptop, gpu"
          error={error ?? undefined}
          wrapperClassName="min-w-0 flex-1"
          className="font-mono"
          disabled={busy}
        />
        <IconButton label="Save labels" icon={<Check />} size="sm" onClick={() => void save()} loading={busy} />
        {v.set && <IconButton label="Use the agent's labels" icon={<RotateCcw />} size="sm" onClick={() => void reset()} disabled={busy} />}
        <IconButton label="Cancel" icon={<X />} size="sm" onClick={() => setEditing(false)} disabled={busy} />
      </div>
    );
  }

  // Its own labels, then the OS one, all one quiet chip style; past `max` they fold into "+N".
  const all = [...own, m.os];
  const shown = all.slice(0, max);
  const rest = all.slice(max);
  const describe = (l: string) => (l === m.os ? `${l} (from its OS, always present)` : l);
  return (
    <span className={cn('group/labels flex min-w-0 items-center gap-1', align === 'end' ? 'justify-end' : 'justify-start', className)}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        className={cn('flex min-w-0 items-center gap-1 rounded-control', wrap ? 'flex-wrap' : 'whitespace-nowrap')}
      >
        {/* Named by a lead-in plus the chips it shows, so the name contains the visible text (WCAG 2.5.3). */}
        <span className="sr-only">Edit labels for {m.name}:</span>
        {shown.map((l) => (
          <Badge key={l} variant="outline" title={describe(l)}>
            {l}
          </Badge>
        ))}
        {rest.length > 0 && (
          <Tooltip content={rest.map(describe).join(', ')}>
            <span className="text-caption text-fg-subtle num">+{rest.length}</span>
          </Tooltip>
        )}
      </button>
      <Pencil className="size-3 shrink-0 text-fg-subtle opacity-0 transition-opacity group-hover/labels:opacity-100" aria-hidden />
    </span>
  );
}

/**
 * Disable new work on a machine (the drain), or enable it again, with an undo toast. Reversible,
 * so it never asks first. Throws on failure, so the caller shows the error where it happened.
 */
export async function setMachineWork(m: Machine, enable: boolean, running: number, reason = ''): Promise<void> {
  if (enable) {
    await resumeNode(m.id);
    toast.success(`${m.name} takes new work again`, 'Queued missions that fit start on it.');
    return;
  }
  await drainNode(m.id, reason);
  toast(`${m.name} disabled for new work`, {
    description: running ? `${running} running ${running === 1 ? 'session finishes' : 'sessions finish'}; nothing new starts.` : 'Nothing new starts on it.',
    action: { label: 'Undo', onClick: () => void resumeNode(m.id).catch(() => undefined) },
  });
}

/** Disable or enable new work, its error in a toast (the Capacity table's row button). */
export async function toggleDrain(m: Machine, drained: boolean, running: number, reason = ''): Promise<void> {
  try {
    await setMachineWork(m, drained, running, reason);
  } catch (e) {
    toast.error(e);
  }
}

